import React, { useEffect, useState } from 'react';
import {
    Save, AlertTriangle, Microscope, ShieldAlert, ArrowUp, ArrowDown, Minus, TrendingUp, CheckCircle2,
} from 'lucide-react';
import { toast } from 'react-toastify';
import LabServices from '../../../shared/ApiServices/LabServices';
import { Dialog, Button, TextArea } from '../../../shared/custom-ui';
import useLabPermissions from '../useLabPermissions';
import {
    extractLabResultId, normalizeResultStatus, extractLabResultsList,
    extractTestParametersList, extractLabOrdersList, extractLabOrderId,
    normalizeSampleStatus, SAMPLE_STATUS_META,
    PARAM_FLAG_META,
    type LabResult, type LabResultParameter, type LabSample, type LabOrder,
} from '../labUtils';
import ParameterTrendChart from '../ParameterTrendChart';
import ResultAuditTrail from '../ResultAuditTrail';

interface ResultEntryModalProps {
    orderId: string;
    order?: LabOrder;
    testId: string;
    testName: string;
    /** Existing result for this test, if one has already been started/submitted/approved. Null for a brand-new entry. */
    existingResult: LabResult | null;
    samples: LabSample[];
    onClose: () => void;
    /** Refresh callback — called after any mutation so the parent row list re-reads the latest state. */
    onChange: () => void;
}

// Result-entry modal — opens already scoped to one test (no in-modal test picker; the
// triggering row in OrderWorkflowList already determined which test this is for).
// Purely for viewing/editing parameters, findings and impression, then Save — the
// workflow moves (Verify, Submit, Approve, Reject) live on the result card in the list.
export const ResultEntryModal: React.FC<ResultEntryModalProps> = ({
    orderId, order, testId, testName, existingResult, samples, onClose, onChange,
}) => {
    const { canEnterResults, currentUser } = useLabPermissions();

    const [selectedResult, setSelectedResult] = useState<LabResult | null>(existingResult);

    // Keep in sync with the parent's copy — e.g. after Verify/Submit/Approve updates the
    // result behind this modal, existingResult changes but this modal was already open.
    useEffect(() => { setSelectedResult(existingResult); }, [existingResult]);
    const [parameters, setParameters] = useState<LabResultParameter[]>([]);
    const [findings, setFindings] = useState(existingResult?.findings ?? '');
    const [impression, setImpression] = useState(existingResult?.impression ?? '');
    const [loadingParams, setLoadingParams] = useState(true);
    const [saving, setSaving] = useState(false);
    const [trendParam, setTrendParam] = useState<string | null>(null);

    // Delta check — patient's most recent prior completed result for the same test
    const [priorParams, setPriorParams] = useState<LabResultParameter[]>([]);

    // Load this test's parameter template, merged with any existing result values
    useEffect(() => {
        let cancelled = false;
        setLoadingParams(true);
        (async () => {
            try {
                const templateParams = extractTestParametersList(await LabServices.getTestParameters(testId));
                const existingParams = existingResult?.parameters ?? [];
                const merged = templateParams.length > 0
                    ? templateParams.map(tp => {
                        const existing = existingParams.find(ep => (ep.paramName ?? (ep as any).param_name) === (tp.paramName ?? (tp as any).param_name));
                        return existing ? { ...tp, value: existing.value ?? '' } : { ...tp, value: '' };
                    })
                    : existingParams;
                if (!cancelled) setParameters(merged);
            } catch { if (!cancelled) setParameters(existingResult?.parameters ?? []); }
            finally { if (!cancelled) setLoadingParams(false); }
        })();
        return () => { cancelled = true; };
    }, [testId, existingResult]);

    // Delta check against patient's prior completed result for this same test
    useEffect(() => {
        let cancelled = false;
        const mobile = order?.mobile ?? '';
        if (!mobile) { setPriorParams([]); return; }
        (async () => {
            try {
                const res = await LabServices.listOrders({ mobile, status: 'completed', limit: 10 });
                const priorOrders = extractLabOrdersList(res)
                    .filter(o => extractLabOrderId(o) !== orderId)
                    .sort((a, b) => new Date(b.orderDate ?? b.createdAt ?? (b as any).created_at ?? 0).getTime()
                        - new Date(a.orderDate ?? a.createdAt ?? (a as any).created_at ?? 0).getTime())
                    .slice(0, 5);
                for (const po of priorOrders) {
                    if (cancelled) return;
                    const priorResults = extractLabResultsList(await LabServices.getOrderResults(extractLabOrderId(po)));
                    const match = priorResults.find(r => (r.testId ?? (r as any).test_id) === testId && (r.parameters?.length ?? 0) > 0);
                    if (match) { if (!cancelled) setPriorParams(match.parameters ?? []); return; }
                }
                if (!cancelled) setPriorParams([]);
            } catch { if (!cancelled) setPriorParams([]); }
        })();
        return () => { cancelled = true; };
    }, [order?.mobile, testId, orderId]);

    const refreshSelectedResult = async (rId: string) => {
        if (!rId || !orderId) return;
        try {
            const fresh = await LabServices.getResult(orderId, rId);
            const obj = fresh?.result ?? fresh;
            if (obj && (obj.resultId ?? obj.SK ?? obj.result_id)) setSelectedResult(obj as any);
        } catch { /* non-fatal — keep existing state */ }
    };

    const buildResultPayload = (extra: Record<string, any> = {}) => ({
        testId,
        findings, impression,
        parameters: parameters.map(p => ({
            paramName: p.paramName ?? (p as any).param_name ?? '',
            value: p.value ?? '',
            unit: p.unit ?? '',
            referenceRange: p.referenceRange ?? (p as any).reference_range ?? '',
            criticalLow: p.criticalLow,
            criticalHigh: p.criticalHigh,
        })),
        ...extra,
    });

    const handleSaveDraft = async () => {
        if (!orderId || !testId) return;
        setSaving(true);
        try {
            const payload = buildResultPayload({ enteredBy: currentUser?.name ?? currentUser?.id ?? undefined });
            if (selectedResult && extractLabResultId(selectedResult)) {
                const rId = extractLabResultId(selectedResult);
                const res: any = await LabServices.updateResult(rId, payload);
                toast.success(res?.message ?? 'Result updated');
                await refreshSelectedResult(rId);
            } else {
                const res = await LabServices.saveDraftResult(orderId, payload);
                const newResultId = res?.resultId ?? res?.result_id ?? res?.SK;
                if (newResultId) {
                    setSelectedResult({ ...payload, resultId: newResultId, reportStatus: 'draft', status: 'draft' } as any);
                    await refreshSelectedResult(newResultId);
                }
                toast.success(res?.message ?? 'Draft saved');
            }
            onChange();
        } catch (err: any) { toast.error(err?.message ?? 'Save failed'); } finally { setSaving(false); }
    };

    const currentStatus = selectedResult
        ? normalizeResultStatus((selectedResult as any).reportStatus ?? selectedResult.status)
        : 'draft';
    const isApproved = currentStatus === 'approved';
    const isRejected = currentStatus === 'rejected';
    const canEdit = !isApproved && !isRejected && canEnterResults;

    const parseNum = (v?: string) => { const n = Number(v); return v != null && v !== '' && !isNaN(n) ? n : null; };
    const isParamCritical = (p: LabResultParameter): boolean => {
        const val = parseNum(p.value);
        if (val === null) return false;
        const lo = parseNum(p.criticalLow);
        const hi = parseNum(p.criticalHigh);
        return (lo !== null && val < lo) || (hi !== null && val > hi);
    };
    const criticalParams = parameters.filter(isParamCritical);
    const hasCritical = criticalParams.length > 0;
    const isCriticalAcknowledged = Boolean((selectedResult as any)?.criticalAcknowledgedBy);

    const verifiedByUser = (selectedResult as any)?.verifiedBy as string | undefined;

    // Laboratory Accessioning gate — informational only here; entering results is
    // still allowed at Collected/In-Transit, this is just a reminder while editing.
    const gatedSample = samples.find(sm => (sm.testId ?? (sm as any).test_id) === testId) ?? null;
    const gatedStatus = gatedSample ? normalizeSampleStatus(gatedSample) : null;
    const sampleRejectsEntry = gatedStatus === 'rejected';

    return (
        <>
            <Dialog
                isOpen
                onClose={onClose}
                size="full"
                title={testName}
                description={selectedResult ? undefined : 'Recording a new result for this test.'}
                footer={
                    <div className="flex items-center justify-end gap-1.5">
                        <Button variant="outline" size="small" onClick={onClose}>Cancel</Button>
                        {canEdit && (
                            <Button variant="primary" size="small" onClick={handleSaveDraft} loading={saving} disabled={sampleRejectsEntry}
                                title={sampleRejectsEntry ? 'Cannot save results against a rejected specimen' : undefined}
                                leftIcon={!saving ? <Save className="w-full h-full" strokeWidth={2} /> : undefined}>
                                Save
                            </Button>
                        )}
                    </div>
                }
            >
                <div className="flex flex-col gap-5">
                    {loadingParams && (
                        <div className="flex flex-col items-center justify-center py-16 gap-3">
                            <div className="w-7 h-7 rounded-full border-[3px] border-[var(--v2-primary-200,rgba(4,94,184,0.2))] border-t-[var(--v2-primary-500)] animate-spin" />
                            <span className="text-[12px] font-bold text-[var(--v2-text-muted)]">Loading test parameters…</span>
                        </div>
                    )}

                    {/* Verification status — informational only; the Verify action lives on the result card */}
                    {!loadingParams && currentStatus === 'draft' && selectedResult && extractLabResultId(selectedResult) && (
                        <div className="flex items-center gap-2.5 p-3 rounded-xl bg-amber-50 border border-amber-200">
                            <div className="w-8 h-8 rounded-lg bg-amber-100 flex items-center justify-center flex-shrink-0 text-amber-600">
                                <ShieldAlert className="w-4 h-4" strokeWidth={2} />
                            </div>
                            <div className="flex flex-col gap-0">
                                <h4 className="text-[12px] font-bold text-amber-800 m-0">
                                    {verifiedByUser ? 'Verified' : 'Verification Required'}
                                </h4>
                                <p className="text-[11px] font-medium text-amber-700 m-0">
                                    {verifiedByUser ? `Verified by ${verifiedByUser}` : 'This entry is a draft and should be verified before submission.'}
                                </p>
                            </div>
                        </div>
                    )}

                    {/* Accessioning Warning — allowed to enter results at Collected/In-Transit, just a reminder */}
                    {!loadingParams && gatedStatus && gatedStatus !== 'accessioned' && !isApproved && (
                        <div className="rounded-xl p-3 flex items-start sm:items-center gap-2.5 border" style={{
                            background: sampleRejectsEntry ? 'rgba(220,38,38,0.05)' : 'rgba(217,119,6,0.05)',
                            borderColor: sampleRejectsEntry ? 'rgba(220,38,38,0.2)' : 'rgba(217,119,6,0.2)',
                        }}>
                            <div className="w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0" style={{ background: sampleRejectsEntry ? 'rgba(220,38,38,0.1)' : 'rgba(217,119,6,0.1)' }}>
                                <AlertTriangle className="w-4 h-4" style={{ color: sampleRejectsEntry ? '#DC2626' : '#D97706' }} strokeWidth={2} />
                            </div>
                            <div className="flex flex-col gap-0">
                                <h4 className="text-[12px] font-bold m-0" style={{ color: sampleRejectsEntry ? '#B91C1C' : '#B45309' }}>Accessioning Warning</h4>
                                <p className="text-[11px] font-medium m-0 leading-snug" style={{ color: sampleRejectsEntry ? '#DC2626' : '#D97706' }}>
                                    {sampleRejectsEntry
                                        ? `Specimen rejected${gatedSample?.rejectionReason ? ` — ${gatedSample.rejectionReason}` : ''}. Results cannot be entered against a rejected sample.`
                                        : `Specimen not yet accessioned by the lab (currently: ${SAMPLE_STATUS_META[gatedStatus].label}). Confirm receipt before finalizing results.`}
                                </p>
                            </div>
                        </div>
                    )}

                    {/* Critical value status — informational only; acknowledgment happens from the result card */}
                    {!loadingParams && hasCritical && (
                        <div className="rounded-xl p-3 flex items-center gap-2.5 border bg-red-50 border-red-200">
                            <div className="w-8 h-8 rounded-lg bg-red-100 flex items-center justify-center flex-shrink-0">
                                <ShieldAlert className="w-4 h-4 text-red-600" strokeWidth={2} />
                            </div>
                            <div className="flex flex-col gap-0 flex-1">
                                <h4 className="text-[12px] font-bold text-red-700 m-0">Critical Value Detected</h4>
                                <p className="text-[11px] font-medium text-red-600 m-0">Parameter{criticalParams.length > 1 ? 's' : ''}: {criticalParams.map(p => p.paramName ?? (p as any).param_name).join(', ')}</p>
                            </div>
                            {isCriticalAcknowledged && (
                                <span className="inline-flex items-center gap-1.5 text-[11px] font-bold px-2.5 py-1 rounded-lg bg-green-100 text-green-700 border border-green-200 flex-shrink-0">
                                    <CheckCircle2 className="w-3.5 h-3.5" strokeWidth={2.5} /> Acknowledged
                                </span>
                            )}
                        </div>
                    )}

                    {!loadingParams && parameters.length > 0 && (
                        <div className="rounded-xl border border-[var(--v2-border-card)] bg-[var(--v2-bg-card)] overflow-hidden flex flex-col">
                            <div className="px-4 py-2.5 border-b border-[var(--v2-border-subtle)] bg-[var(--v2-bg-surface)] flex items-center gap-2">
                                <Microscope className="w-4 h-4" style={{ color: 'var(--v2-primary-500)' }} />
                                <h4 className="text-[12px] font-bold text-[var(--v2-text-heading)] m-0">Clinical Parameters</h4>
                            </div>
                            <div className="overflow-x-auto w-full">
                                <table className="w-full text-left min-w-[700px]">
                                    <thead>
                                        <tr className="border-b border-[var(--v2-border-subtle)]">
                                            {['Parameter', 'Result Value', 'Unit', 'Ref. Range', 'Prior', 'Flag', ''].map(h => (
                                                <th key={h} className="px-4 py-2.5 font-bold text-[var(--v2-text-secondary)] text-[10px] uppercase tracking-wide whitespace-nowrap bg-[var(--v2-bg-surface)]">{h}</th>
                                            ))}
                                        </tr>
                                    </thead>
                                    <tbody className="divide-y divide-[var(--v2-border-subtle)]">
                                        {parameters.map((p, i) => {
                                            const apiFlag = (p.flag ?? '').toUpperCase();
                                            const value = Number(p.value ?? 0);
                                            const refRange = p.referenceRange ?? (p as any).reference_range ?? '';
                                            const [lo, hi] = refRange.split('-').map((s: string) => Number(s.trim()));
                                            const derivedAbnormal = refRange && !isNaN(lo) && !isNaN(hi) && value !== 0 && (value < lo || value > hi);
                                            const critical = isParamCritical(p);
                                            const flagKey = critical ? 'C' : (apiFlag || (derivedAbnormal ? (value > hi ? 'H' : 'L') : 'N'));
                                            const flagMeta = PARAM_FLAG_META[flagKey] ?? PARAM_FLAG_META['N'];
                                            const isAbnormal = flagKey !== 'N';
                                            const paramName = p.paramName ?? (p as any).param_name ?? '';
                                            const priorMatch = priorParams.find(pp => (pp.paramName ?? (pp as any).param_name) === paramName);
                                            const priorVal = priorMatch ? parseNum(priorMatch.value) : null;
                                            const curVal = parseNum(p.value);
                                            return (
                                                <tr key={i} className={`transition-colors hover:bg-[var(--v2-bg-surface)] ${critical ? 'bg-red-50/30' : isAbnormal ? 'bg-orange-50/30' : ''}`}>
                                                    <td className="px-4 py-2.5">
                                                        <span className="text-[12px] font-bold text-[var(--v2-text-primary)]">{paramName || 'Unnamed Parameter'}</span>
                                                    </td>
                                                    <td className="px-4 py-2 w-36">
                                                        {canEdit ? (
                                                            <input type="text"
                                                                className={`w-full rounded-lg border px-3 py-1.5 text-[13px] font-bold outline-none transition-colors ${critical ? 'border-red-300 text-red-700 focus:border-red-500 focus:ring-2 focus:ring-red-200' : isAbnormal ? 'border-orange-300 text-orange-700 focus:border-orange-500 focus:ring-2 focus:ring-orange-200' : 'border-[var(--v2-border-interactive)] text-[var(--v2-text-primary)] focus:border-[var(--v2-primary-500)] focus:ring-2 focus:ring-[var(--v2-primary-500)]/20'}`}
                                                                value={p.value ?? ''}
                                                                placeholder="Enter value"
                                                                onChange={e => setParameters(ps => ps.map((r, j) => j === i ? { ...r, value: e.target.value } : r))}
                                                            />
                                                        ) : (
                                                            <span className={`text-[14px] font-bold ${critical ? 'text-red-600' : isAbnormal ? 'text-orange-600' : 'text-[var(--v2-text-primary)]'}`}>{p.value ?? '—'}</span>
                                                        )}
                                                    </td>
                                                    <td className="px-4 py-2.5 text-[var(--v2-text-secondary)] font-medium text-[11px]">{p.unit || '—'}</td>
                                                    <td className="px-4 py-2.5 text-[var(--v2-text-muted)] font-mono text-[10px] whitespace-nowrap">{refRange || '—'}</td>
                                                    <td className="px-4 py-2.5">
                                                        {priorVal !== null && curVal !== null ? (
                                                            <div className="flex items-center gap-1 px-1.5 py-0.5 rounded bg-[var(--v2-bg-surface)] w-fit border border-[var(--v2-border-subtle)]" title={`Previous visit: ${priorMatch?.value}`}>
                                                                {curVal > priorVal ? <ArrowUp className="w-3 h-3 text-red-500" strokeWidth={3} />
                                                                    : curVal < priorVal ? <ArrowDown className="w-3 h-3 text-blue-500" strokeWidth={3} />
                                                                        : <Minus className="w-3 h-3 text-[var(--v2-text-muted)]" strokeWidth={3} />}
                                                                <span className="text-[10px] font-bold text-[var(--v2-text-secondary)]">{priorMatch?.value}</span>
                                                            </div>
                                                        ) : <span className="text-[10px] text-[var(--v2-text-muted)]">—</span>}
                                                    </td>
                                                    <td className="px-4 py-2.5">
                                                        <span className="inline-flex items-center text-[9px] font-bold px-2 py-0.5 rounded-md uppercase tracking-wide"
                                                            style={{ background: flagMeta.bg, color: flagMeta.color }}>
                                                            {flagMeta.label}
                                                        </span>
                                                    </td>
                                                    <td className="px-4 py-2.5 text-right">
                                                        <button type="button" title="View trend chart" onClick={() => setTrendParam(paramName)}
                                                            className="w-7 h-7 rounded-lg flex items-center justify-center cursor-pointer border-0 bg-transparent text-[var(--v2-text-muted)] hover:bg-blue-50 hover:text-blue-600 transition-colors">
                                                            <TrendingUp className="w-3.5 h-3.5" strokeWidth={2} />
                                                        </button>
                                                    </td>
                                                </tr>
                                            );
                                        })}
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    )}

                    {!loadingParams && (
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <TextArea
                                label="Findings"
                                rows={3} value={findings} onChange={e => setFindings(e.target.value)} readOnly={!canEdit}
                                placeholder="Enter detailed clinical findings..." />
                            <TextArea
                                label="Impression"
                                rows={3} value={impression} onChange={e => setImpression(e.target.value)} readOnly={!canEdit}
                                placeholder="Enter clinical impression and conclusion..." />
                        </div>
                    )}

                    {selectedResult && (
                        <div className="border-t border-[var(--v2-border-subtle)] pt-4 -mx-6 px-6">
                            <ResultAuditTrail result={selectedResult} />
                        </div>
                    )}
                </div>
            </Dialog>

            {trendParam && (
                <ParameterTrendChart
                    mobile={order?.mobile ?? ''}
                    testId={testId}
                    paramName={trendParam}
                    currentOrderId={orderId}
                    onClose={() => setTrendParam(null)}
                />
            )}
        </>
    );
};

export default ResultEntryModal;
